import React from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import type { NegotiationAutoTopup, PutAutoTopupInput } from "../../services/negotiationBalance";
import { BannerFeedback } from "../BannerFeedback";
import { Button } from "../Button";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import {
  DEFAULT_MIN_TOPUP_CENTS,
  INELIGIBLE_COPY,
  parseWholeReais,
} from "./negotiationBalanceFormat";
import "./NegotiationBalance.css";

/**
 * Where the company stands on the Saldo de Negociações conditions. The weekly
 * automatic top-up is a top-up too, so (owner decision 11) it can be turned
 * on only once the version /termos publishes has been accepted.
 */
export type AutoTopupTermsGate = "accepted" | "required" | "in_review";

export type AutoTopupSettingsProps = {
  autoTopup: NegotiationAutoTopup;
  terms: AutoTopupTermsGate;
  /** Opens the conditions acceptance; absent offers no shortcut. */
  onAcceptTerms?: () => void;
  /** Smallest fixed amount Payments accepts, in cents. */
  minAmountCents?: number;
  saving?: boolean;
  /** Application-authored copy only. */
  error?: string;
  /** Initial state of the bank-limit acknowledgement. */
  defaultAcknowledged?: boolean;
  onSave: (input: PutAutoTopupInput) => void;
};

/** Largest fixed amount of Payments' auto top-up CHECK (auto_topup_cents), in cents. */
const MAX_AUTO_TOPUP_CENTS = 99_999_999_900;

const checkboxSX = {
  color: "var(--neutral-11)",
  "&.Mui-checked": { color: "var(--brand-9)" },
};

const checkboxLabelSX = {
  alignItems: "flex-start",
  marginLeft: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "13px",
    color: "var(--neutral-11)",
    paddingTop: "var(--spacing-md)",
  },
};

export const AUTO_TOPUP_LINE_COPY =
  "Cobrado como linha separada na mesma cobrança semanal do Pix Automático: uma autorização, um débito, uma tarifa.";
export const AUTO_TOPUP_ACK_COPY =
  "O banco da empresa pode ter limite próprio para o débito automático; ele precisa cobrir serviços + recarga + R$ 3,50.";

/**
 * Optional weekly automatic top-up of a fixed amount, carried as its own line
 * of the same weekly Pix Automático charge. Rendered only when Payments offers
 * it (`autoTopup.available`) and only for `org_payments` edit.
 *
 * Turning it on always requires the accepted conditions and the
 * acknowledgement that the payer bank's own debit limit must cover services,
 * the top-up and the Pix fee: a refusal by the bank would refuse the whole
 * weekly debit, radio and TV included. Turning it off never requires either.
 * @story args { terms: "accepted", autoTopup: { available: true, mode: "off", version: 1, eligible: true } }
 * @story state TermsRequired { terms: "required" }
 * @story state TermsInReview { terms: "in_review", onAcceptTerms: undefined }
 * @story state Enabled { autoTopup: { available: true, mode: "weekly_fixed", amountCents: 50000, version: 3, eligible: true }, defaultAcknowledged: true }
 * @story state NeedsPixAutomatico { autoTopup: { available: true, mode: "off", version: 1, eligible: false, ineligibleReason: "pix_automatico_not_approved" } }
 * @story state BankLimitSuspected { autoTopup: { available: true, mode: "off", version: 4, eligible: false, ineligibleReason: "bank_limit_suspected" } }
 * @story state Saving { autoTopup: { available: true, mode: "weekly_fixed", amountCents: 50000, version: 3, eligible: true }, defaultAcknowledged: true, saving: true }
 */
export function AutoTopupSettings({
  autoTopup,
  terms,
  onAcceptTerms,
  minAmountCents = DEFAULT_MIN_TOPUP_CENTS,
  saving = false,
  error,
  defaultAcknowledged = false,
  onSave,
}: AutoTopupSettingsProps) {
  const fieldId = React.useId();
  const enabledNow = autoTopup.mode === "weekly_fixed";
  const [enabled, setEnabled] = React.useState(enabledNow);
  const [draft, setDraft] = React.useState(
    autoTopup.amountCents ? String(Math.floor(autoTopup.amountCents / 100)) : "",
  );
  const [acknowledged, setAcknowledged] = React.useState(defaultAcknowledged);

  // A new answer from Payments (another session saved, or a save landed)
  // resets the draft to what is stored.
  React.useEffect(() => {
    setEnabled(autoTopup.mode === "weekly_fixed");
    setDraft(autoTopup.amountCents ? String(Math.floor(autoTopup.amountCents / 100)) : "");
  }, [autoTopup.version, autoTopup.mode, autoTopup.amountCents]);

  const reais = parseWholeReais(draft);
  const amountCents = reais === null ? null : reais * 100;
  const amountValid =
    amountCents !== null && amountCents >= minAmountCents && amountCents <= MAX_AUTO_TOPUP_CENTS;
  // An ineligible company, or one without the accepted conditions, can still
  // turn an active setting off, never on.
  const canTurnOn = autoTopup.eligible && terms === "accepted";
  const changed = enabled
    ? !enabledNow || amountCents !== (autoTopup.amountCents ?? null)
    : enabledNow;
  const savable = enabled ? canTurnOn && amountValid && acknowledged && changed : changed;

  function save() {
    if (!savable || saving) return;
    if (enabled && amountCents !== null) {
      onSave({
        mode: "weekly_fixed",
        amountCents,
        version: autoTopup.version,
        bankLimitAcknowledged: true,
      });
    } else {
      onSave({ mode: "off", version: autoTopup.version });
    }
  }

  return (
    <div className="negotiationAutoTopup" data-testid="negotiation-auto-topup">
      <BannerFeedback type="error" open={Boolean(error)} message={error ?? ""} />
      <p className="negotiationBalanceText">{AUTO_TOPUP_LINE_COPY}</p>
      <p className="negotiationBalanceHint">
        O crédito entra quando o débito semanal for liquidado.
      </p>
      {terms === "required" && (
        <div className="negotiationAutoTopupTerms" data-testid="negotiation-auto-topup-terms">
          <p className="negotiationBalanceHint" role="status">
            A recarga automática também é uma recarga: antes de ativá-la, a empresa aceita as
            condições do Saldo de Negociações.
          </p>
          {onAcceptTerms && (
            <Button callback={onAcceptTerms} disabled={saving}>
              Ver as condições
            </Button>
          )}
        </div>
      )}
      {terms === "in_review" && (
        <p
          className="negotiationBalanceHint"
          role="status"
          data-testid="negotiation-auto-topup-terms-in-review"
        >
          As condições do Saldo de Negociações estão em revisão. A recarga automática fica
          disponível quando elas forem publicadas nos Termos de Uso.
        </p>
      )}
      {!autoTopup.eligible && autoTopup.ineligibleReason && (
        <p
          className="negotiationBalanceHint"
          role="status"
          data-testid="negotiation-auto-topup-ineligible"
        >
          {INELIGIBLE_COPY[autoTopup.ineligibleReason]}
        </p>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={saving || (!enabled && !canTurnOn)}
            onChange={(event) => setEnabled(event.target.checked)}
          />
        }
        label="Recarga automática semanal"
      />
      {enabled && (
        <>
          <div className="negotiationAutoTopupField">
            <label htmlFor={fieldId}>Valor fixo por semana (R$, inteiro)</label>
            <input
              id={fieldId}
              inputMode="numeric"
              autoComplete="off"
              value={draft}
              aria-invalid={draft !== "" && !amountValid}
              disabled={saving}
              data-testid="negotiation-auto-topup-amount"
              onChange={(event) => setDraft(event.target.value)}
            />
            <small className="negotiationBalanceHint">Mínimo {formatBRL(minAmountCents)}.</small>
          </div>
          <FormControlLabel
            sx={checkboxLabelSX}
            control={
              <Checkbox
                sx={checkboxSX}
                checked={acknowledged}
                disabled={saving}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
            }
            label={AUTO_TOPUP_ACK_COPY}
          />
        </>
      )}
      <div className="negotiationAutoTopupActions">
        <Button
          callback={save}
          disabled={!savable}
          loading={{ state: saving, text: "Salvando a recarga automática" }}
        >
          Salvar recarga automática
        </Button>
      </div>
    </div>
  );
}
